import { useState } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { Check, Copy, KeyRound, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";

/**
 * Dialog de admin para restablecer la contrasena de OTRO usuario.
 *
 * Flow:
 *  1. Admin hace click en "Restablecer contrasena" desde SalesEdit
 *  2. Dialog se abre con 2 opciones:
 *     a) Generar contrasena aleatoria (16 chars alfanumericos)
 *     b) Escribir una contrasena manual (min 6 chars)
 *  3. Al confirmar, se llama a dataProvider.salesAdminResetPassword
 *  4. Dialog muestra la contrasena resultante con boton "Copiar" para que
 *     el admin la comparta con el usuario por su canal preferido
 *  5. Toast informativo recuerda que hay que compartirla
 *
 * Esto es necesario porque Supabase Auth por default requiere SMTP para
 * enviar emails de invite/reset, y en una demo no esta configurado.
 */

function generateRandomPassword(length = 16): string {
  // Alfabeto sin caracteres ambiguos (sin 0/O/l/I/1) para que sea legible
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    return Array.from(arr, (n) => alphabet[n % alphabet.length]).join("");
  }
  // Fallback (no deberia llegar aqui en navegadores modernos)
  let pwd = "";
  for (let i = 0; i < length; i++) {
    pwd += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return pwd;
}

export function AdminResetPasswordDialog({
  sale,
}: {
  sale: Pick<Sale, "id" | "first_name" | "last_name" | "email">;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedPassword, setSavedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    setPassword(generateRandomPassword());
    setSavedPassword(null);
    setCopied(false);
  };

  const handleSubmit = async () => {
    if (!password || password.length < 6) {
      notify("La contraseña debe tener al menos 6 caracteres", {
        type: "error",
      });
      return;
    }
    setSubmitting(true);
    try {
      await dataProvider.salesAdminResetPassword(sale.id, password);
      setSavedPassword(password);
      notify(
        `Contraseña actualizada para ${sale.first_name}. Cópiala y compártela con el usuario por un canal seguro.`,
        { type: "success" },
      );
    } catch (e: any) {
      notify(e?.message ?? "No se pudo restablecer la contraseña", {
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!savedPassword) return;
    try {
      await navigator.clipboard.writeText(savedPassword);
      setCopied(true);
      notify("Contraseña copiada al portapapeles", { type: "success" });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      notify("No se pudo copiar — selecciónala manualmente", {
        type: "error",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Limpiar estado al cerrar
    setTimeout(() => {
      setPassword("");
      setSavedPassword(null);
      setCopied(false);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <KeyRound className="h-4 w-4" />
          Restablecer contraseña
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Restablecer contraseña</DialogTitle>
          <DialogDescription>
            Define una nueva contraseña para <strong>{sale.first_name} {sale.last_name}</strong> ({sale.email}). Tendrás que compartirla con el usuario manualmente.
          </DialogDescription>
        </DialogHeader>

        {savedPassword == null ? (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="reset-password">Nueva contraseña</Label>
              <div className="flex gap-2">
                <Input
                  id="reset-password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoFocus
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleGenerate}
                  title="Generar contraseña aleatoria de 16 caracteres"
                >
                  <Sparkles className="h-4 w-4" />
                  Generar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres. Te recomendamos usar "Generar" para mayor seguridad.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
              <p className="text-sm font-medium mb-2">
                ✅ Contraseña actualizada con éxito
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                Copia esta contraseña y compártela con {sale.first_name} por un canal seguro (mensaje directo). Después de su primer login pueden cambiarla desde su perfil.
              </p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono select-all">
                  {savedPassword}
                </code>
                <Button
                  type="button"
                  variant={copied ? "default" : "outline"}
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {savedPassword == null ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !password || password.length < 6}
              >
                {submitting ? "Aplicando..." : "Aplicar contraseña"}
              </Button>
            </>
          ) : (
            <Button type="button" onClick={handleClose}>
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
